<template>
    <BaseCard padding="lg" class="flex-col gap-6 w-full max-w-lg">
        <h1 class="text-ink title-subsection text-center">{{ t("onboarding.checkin.title") }}</h1>
        <div class="flex flex-col gap-3 my-6">
            <ScheduleFollowUp
                :is-collapsed="true"
                :date="scheduleDate"
                :recurring="scheduleRecurring"
                :disabled="loading"
                :variant="ScheduleFollowUpVariant.FULL"
                :title="title"
                :description="description"
                @date-update="handleDateUpdate"
                @recurring-update="handleRecurringUpdate" />
        </div>
        <BaseNotification v-if="teamCompletionFailed" theme="error" hide-dismiss :text="t('teamJoin.failed')" />
        <BaseButton v-if="teamCompletionFailed" theme="secondary" :disabled="loading" @click="goHome">{{ t("teamJoin.retry") }}</BaseButton>
        <div class="w-full flex flex-col items-center gap-4">
            <BaseButton theme="primary" class="w-fit" :disabled="loading || scheduled" @click="handleSchedule">{{ t("onboarding.checkin.confirmButton") }}</BaseButton>
            <BaseButton theme="tertiary" :disabled="loading" :class="{ 'skip-loading': loading }" data-testid="skip-personalization" @click="goHome">{{
                t("onboarding.checkin.skipButton")
            }}</BaseButton>
        </div>
    </BaseCard>
</template>

<script setup>
import { router } from "@inertiajs/vue3";
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import ScheduleFollowUp, { ScheduleFollowUpVariant } from "~vue/ScheduleFollowUp.vue";
import { createTeamInviteOnboarding } from "~vue/Teams/completeTeamInviteOnboarding";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { DateTime } from "luxon";
import { inject, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
    title: String,
    description: String,
    redirectPath: String,
    teamInvitation: Boolean,
});

const { $sendEvent } = inject("globalProperties");

const scheduleDate = ref(initializeDate());
const scheduleRecurring = ref(true);
const loading = ref(false);
const scheduled = ref(false);
const userDetails = inject("user_details");
const teamInviteOnboarding = createTeamInviteOnboarding(() => userDetails?.value);
const teamCompletionFailed = ref(false);

function initializeDate() {
    const date = new Date(Date.now());
    date.setDate(date.getDate() + 5); // default to 5 business days later

    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0) {
        // Sunday
        date.setDate(date.getDate() + 1); // Move to Monday
    } else if (dayOfWeek === 6) {
        // Saturday
        date.setDate(date.getDate() + 2); // Move to Monday
    }

    return date;
}

function handleDateUpdate(newDate) {
    scheduleDate.value = newDate;
}

function handleRecurringUpdate(recurring) {
    scheduleRecurring.value = recurring;
}

async function handleSchedule() {
    try {
        loading.value = true;
        await $sendEvent("schedule_follow_up", {
            event_description: `${props.title}: ${props.description}`,
            event_at_confirmed: DateTime.fromJSDate(scheduleDate.value).toISO(),
            event_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            recurring: scheduleRecurring.value,
            type: ChatAction.FOLLOW_UP,
        });
        scheduled.value = true;
        logUserInteraction(AmplitudeEventNames.FOLLOWUP_SCHEDULED, { source: "onboarding_check_in" });
        await goHome();
    } catch (e) {
        logError(e);
    } finally {
        loading.value = false;
    }
}

async function goHome() {
    if (props.teamInvitation) {
        loading.value = true;
        teamCompletionFailed.value = false;
        try {
            const result = await teamInviteOnboarding.finish();
            if (result !== "not_invited") {
                teamCompletionFailed.value = result === "failed";
                return;
            }
        } catch (error) {
            logError(error);
            teamCompletionFailed.value = true;
            return;
        } finally {
            loading.value = false;
        }
    }
    router.visit(props.redirectPath, {
        onSuccess: () => {
            logUserInteraction("onboarding_home_reached", {});
        },
    });
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .skip-loading {
        @apply hidden md:flex;
    }
}
</style>
